import React, { useState } from 'react'
import PropTypes from 'prop-types'
import styled from 'styled-components'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useTranslation, Trans } from 'react-i18next'
import { setGlobalModal } from 'actions/index'
import { updateRequestDelay } from 'actions/review_automation'
import { sendCtaClickEvent } from 'helpers/segment'
import { NO_ACCESS_LIMIT } from 'constants/feature_limits'
import { hasAccess } from 'helpers/permissions'
import { PERMISSION_KEYS } from 'constants/account/permissions'
import { BaseModalLayout } from './BaseModalLayout'
import { EditReviewAutomationSettingsInvalidError } from './EditReviewAutomationSettings'
import { EditReviewAutomationSettingsInput } from './EditReviewAutomationSettingsInput'

const DescriptionWrapper = styled.div`
  margin: 32px 0px;
  padding-right: 24px;
  b {
    font-size: 12px;
  }
`

const SettingsModal = ({ fromOrder }) => {
  const dispatch = useDispatch()
  const { t } = useTranslation(['review_automation', 'generic'])

  const inventoryLimit = useSelector(
    state => state.globalData.featureLimits.inventoryManager.featureLimit
  )
  const permissions = useSelector(state => state.globalData.user.permissions)
  const myProductsAccess = hasAccess(permissions, PERMISSION_KEYS.MY_PRODUCTS)
  const renderProductSettings =
    inventoryLimit !== NO_ACCESS_LIMIT && myProductsAccess

  const marketplaceDelay = useSelector(
    state =>
      state.amazonSellerAccounts?.amazonSellerAccountSelector?.selectedCountry
        ?.review_request_delay
  )

  const [delay, setDelay] = useState(marketplaceDelay)

  const onSubmit = () => {
    sendCtaClickEvent({
      text: 'Marketplace Review Request Delay Time Changed',
      location: 'Review Automation'
    })
    dispatch(updateRequestDelay(delay))
  }

  const invalid = delay < 5 || delay > 30

  const backToOrderModalAction = () => {
    dispatch(setGlobalModal('OrderReviewModal'))
  }

  return (
    <BaseModalLayout
      title={t(
        'review_automation:SettingsModal.title',
        'Review Automation Settings'
      )}
      content={
        <>
          {t(
            'review_automation:SettingsModal.subtitle',
            'Review requests will be sent after your customers receive their product. You can set a default time to delay the requests for each marketplace.'
          )}

          <DescriptionWrapper>
            <Trans i18nKey="review_automation:SettingsModal.description">
              <b>The currently selected marketplace&#39;s</b> default review
              request time is
              <EditReviewAutomationSettingsInput
                value={delay}
                invalid={invalid}
                onChange={value => setDelay(Number.parseInt(value, 10))}
              />
              days.
            </Trans>{' '}
            {invalid && <EditReviewAutomationSettingsInvalidError />}
          </DescriptionWrapper>

          {renderProductSettings && (
            <Trans i18nKey="review_automation:SettingsModal.footer">
              To change the review request time for a specific product, go to{' '}
              <Link to="/products" onClick={() => dispatch(setGlobalModal())}>
                Product Settings.
              </Link>
            </Trans>
          )}
        </>
      }
      cancelButton={{
        label: t('generic:Back', 'Back'),
        skipClose: fromOrder,
        onClick: fromOrder ? backToOrderModalAction : undefined
      }}
      submitButton={{
        label: t(
          'review_automation:SettingsModal.submitButton',
          'Set Default Time'
        ),
        disabled: invalid,
        onClick: onSubmit
      }}
    />
  )
}

SettingsModal.propTypes = {
  fromOrder: PropTypes.bool
}

SettingsModal.defaultProps = {
  fromOrder: false
}

export { SettingsModal }
